import { isArray, isPlainObject, isString } from '@dimjs/lang';
import { isNumber, isUndefinedOrNull, LabelValueItem, toArray, TPlainObject } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { dequal } from 'dequal';
import {
  cloneElement,
  forwardRef,
  Fragment,
  ReactElement,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ListItem } from '../../../list/list-item';
import { Progress } from '../../../progress';
import { getFormModalPickerValue, toLabelValue } from '../../helps/utils';
import { FormModalColumnItem, FormModalRefApi } from '../../types';
import './style.less';

export type FormModalProps = {
  className?: string;
  /** isPure = true 无效 */
  placeholder?: string;
  /** isPure = true 无效 */
  adapter?: (value?: FormModalColumnItem) => string | JSX.Element;
  children: ReactElement;
  /** isPure = true 无效 */
  loading?: boolean;
  /**
   * 是否自定义modal
   * ```
   * 与 isPure 的与别在于，存在 ListItem 的结构
   * ```
   */
  isCustom?: boolean;
  /**
   * 纯净模块
   * ```
   * 1. 没有任何结构，只做 modal 的状态代理
   * 2. 纯净模式默认无触发modal操作结构，可通过pureAction自定义处理
   * ```
   */
  isPure?: boolean;
  /**
   * 纯净模块触发节点；isPure = true有效
   * @param
   * ```
   * <FormModal
   *   isPure
   *   pureAction={({ onClick }) => {
   *     return <div onClick={onClick}>点我弹框</div>;
   *   }}
   * >
   *   <Modal>1</Modal>
   * </FormModal>
   * ```
   */
  pureAction?: (data: { onClick: (event) => void }) => ReactElement;
};

type FormModalPropsInner = FormModalProps & {
  value?: FormModalColumnItem;
  onChange: (value?: FormModalColumnItem) => void;
  disabled?: boolean;
  suffix?: JSX.Element | string;
};

/**
 * 自定义FormItem，用于弹框结构
 * ```
 * 弹窗类结构包括两类
 * picker类
 * 1. 包括：DatePicker、DateRangePicker、ModalUpPicker、TabSelect、ModalSelector等选择器弹窗组件
 *    a. 此类组件在取值时，使用form.getFormModalPickerValue()可直接获取value值
 *    b. 赋值结构必须时 value
 * 2. 自定义弹框（设置isCustom=true）
 *    a. 自定义弹框响应数据中，不要存在label、value的定义字段
 *    b. 回填结构必须是 { label、value }结构，其中label用于页面Form.Item中展示
 * ```
 */
export const FormModal = forwardRef<FormModalRefApi, FormModalProps>((props, ref) => {
  const newProps = props as FormModalPropsInner;
  const [visible, visibleChange] = useState(false);
  const selectedDataRef = useRef<LabelValueItem[] | LabelValueItem>([]);
  const domTypeName = newProps?.children?.type['domTypeName'];

  /**
   * props.value格式
   * 1. value
   * 2. { label、value }
   * 3. { label、value: [value1, value2] }
   * 4. { label、value: [{ label、value }, { label、value }] }
   * 5. [ value1, value2 ]
   * 6. [ { label、value }, { label、value } ]
   */
  const labelValue = useMemo(() => {
    if (props.isCustom || props.isPure) {
      return newProps.value;
    }
    if (
      newProps.value === undefined ||
      newProps.value === null ||
      (isArray(newProps.value) && newProps.value.length == 0) ||
      (isPlainObject(newProps.value) && isUndefinedOrNull(newProps.value['value']))
    ) {
      return undefined;
    }

    const baseValue = getFormModalPickerValue(newProps.value);

    if (isUndefinedOrNull(baseValue)) {
      return undefined;
    }

    if (domTypeName === 'DatePicker') {
      return { label: `${baseValue}`, value: baseValue };
    }

    if (domTypeName === 'DateRangePicker' || domTypeName === 'CalendarDrawer') {
      const dateRangeList = toArray<string>(baseValue);
      const dateRangeValue = [{ label: dateRangeList[0], value: dateRangeList[0] }];
      if (dateRangeList.length > 1) {
        dateRangeValue.push({ label: dateRangeList[1], value: dateRangeList[1] });
      }
      return { label: dateRangeList.join('~'), value: dateRangeValue };
    }

    if (isPlainObject(newProps.value) && newProps.value.value && newProps.value.label) {
      return newProps.value as LabelValueItem;
    }

    const selectedDataList = toArray<LabelValueItem>(selectedDataRef.current);
    if (selectedDataList && selectedDataList.length > 0) {
      // 回填数据操作下
      const baseValueList = toArray<string>(baseValue);
      const isDequal = dequal(
        baseValueList,
        selectedDataList.map((item) => item.value),
      );
      if (isDequal) {
        if (isArray(selectedDataRef.current)) {
          return {
            label: selectedDataList.map((item) => item.label).join(''),
            value: selectedDataList,
          };
        } else {
          return selectedDataRef.current;
        }
      } else {
        return {
          label: (isPlainObject(newProps.value) && newProps.value?.label) || baseValueList.join(''),
          value: baseValue,
        };
      }
    }

    if (isString(newProps.value) || isNumber(newProps.value as unknown as number)) {
      return { label: newProps.value, value: newProps.value };
    }
    if (isArray(newProps.value)) {
      /**
       * ['2', '3'] => { label: '23', value: [{ label: '2', value: '2' },{ label: '3', value: '3' }] }
       */
      const labelValueGroup = newProps.value
        .map((item) => {
          return toLabelValue(item as TPlainObject);
        })
        .filter(Boolean) as LabelValueItem[];
      return { label: labelValueGroup.map((item) => item.value).join(''), value: labelValueGroup };
    }

    return { label: newProps.value['value'], value: newProps.value['value'] };
  }, [domTypeName, newProps.value, props.isCustom, props.isPure]);

  const onClose = hooks.useCallbackRef(() => {
    visibleChange(false);
  });

  const onChange = hooks.useCallbackRef((pickItem: FormModalColumnItem) => {
    newProps.onChange?.(pickItem);
    onClose();
  });

  const onClick = hooks.useCallbackRef(() => {
    if (!newProps.disabled) {
      visibleChange(true);
    }
  });

  useImperativeHandle(ref, () => {
    return { onClose };
  });

  const onValueChange = hooks.useCallbackRef((selectedData: LabelValueItem[] | LabelValueItem) => {
    if (props.isCustom) return;
    selectedDataRef.current = selectedData;

    if (isUndefinedOrNull(selectedData)) return;
    /**
     * 回填数据完善 label、value
     * 1. value必须有值
     * 2. value不是完整数据（同时存在label、value为完整数据）
     */
    if (
      isUndefinedOrNull(newProps.value) ||
      (isPlainObject(newProps.value) &&
        !isUndefinedOrNull(newProps.value?.value) &&
        !isUndefinedOrNull(newProps.value?.label))
    ) {
      return;
    } else {
      if (isArray(selectedData) && selectedData.length > 0) {
        newProps.onChange({ label: selectedData.map((item) => item.label).join(''), value: selectedData });
      } else if (isPlainObject(selectedData)) {
        newProps.onChange(selectedData as FormModalColumnItem);
      }
    }
  });

  if (props.isPure) {
    return (
      <Fragment>
        {newProps.pureAction?.({ onClick })}
        <Fragment>
          {cloneElement(newProps.children as JSX.Element, {
            visible,
            value: newProps.value,
            onChange: newProps.onChange,
            onClose: onClose,
          })}
        </Fragment>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <ListItem
        arrow={!props.loading ? true : undefined}
        prefix={!labelValue ? newProps.placeholder : ''}
        onClick={onClick}
        className={newProps.className}
        extra={newProps.suffix}
        icon={<Progress value={0.1} gap={3} size={30} isRotate={true} />}
      >
        {newProps.adapter ? newProps.adapter(labelValue) : labelValue?.label}
      </ListItem>
      <Fragment>
        {cloneElement(newProps.children as JSX.Element, {
          visible,
          value: getFormModalPickerValue(labelValue?.value),
          onChange: onChange,
          onClose: onClose,
          onValueChange,
        })}
      </Fragment>
    </Fragment>
  );
});

FormModal['domTypeName'] = 'Modal';
